import { supabase } from '../supabase'
import { fetchAllPaged } from '../pagination'
import { displayDate } from '../../utils/dates'

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const db = (t: string) => (supabase as any).from(t)

// Methods that live in supplier_payments but are NOT money leaving the business:
//   advance_applied  — an already-paid advance being reallocated onto a PO (no cash)
//   refund_received  — cash coming BACK from the supplier (money in, opposite sign)
// Both must be excluded from a "money we paid to suppliers" report or the totals
// double-count. See migrations 064/065 (advance_applied) and 236 (refund_received).
const EXCLUDED_METHODS = ['advance_applied', 'refund_received']

export interface VendorPaymentFilters {
  from: string        // yyyy-MM-dd inclusive
  to: string          // yyyy-MM-dd inclusive
  search: string      // matches supplier name or reference
  showVoided: boolean // include voided rows (shown struck-through); totals always exclude them
  // Header filters (Feature 4). Empty/undefined = no filter.
  supplierIds?: string[]           // .in('supplier_id', …)
  methods?: string[]               // .in('payment_method', …)
  types?: string[]                 // subset of ['payment','advance'] (derived from purchase_order_id)
  statuses?: string[]              // subset of ['posted','voided']
  amountMin?: number | null
  amountMax?: number | null
}

export interface VendorPaymentRow {
  id: string
  type: 'payment' | 'advance'
  date: string
  supplierName: string
  method: string | null
  reference: string | null
  amount: number
  poNumber: string | null
  journalId: string | null
  journalNumber: string | null
  isVoided: boolean
}

const PAGE_SELECT =
  'id, payment_date, amount, payment_method, reference, purchase_order_id, journal_id, is_voided, ' +
  'supplier_id, suppliers(name), purchase_orders(order_number), journal_entries(entry_number)'

// Strip characters that would break PostgREST's `.or()` mini-grammar.
function sanitizeTerm(term: string): string {
  return term.replace(/[(),]/g, ' ').trim()
}

async function resolveSupplierIds(tid: string, term: string): Promise<string[] | null> {
  if (!term) return null
  const { data } = await db('suppliers')
    .select('id')
    .eq('tenant_id', tid)
    .ilike('name', `%${sanitizeTerm(term)}%`)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return ((data ?? []) as any[]).map(r => r.id as string)
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function applyVendorFilters(q: any, tid: string, filters: VendorPaymentFilters, supplierIds: string[] | null) {
  q = q
    .eq('tenant_id', tid)
    .gte('payment_date', filters.from)
    .lte('payment_date', filters.to)
    // Null-safe exclude of the non-cash methods (a null method is a real payment).
    .or(`payment_method.is.null,payment_method.not.in.(${EXCLUDED_METHODS.join(',')})`)
  if (!filters.showVoided) q = q.not('is_voided', 'is', true)
  if (filters.search) {
    const term = sanitizeTerm(filters.search)
    const ids = supplierIds ?? []
    if (ids.length) q = q.or(`reference.ilike.%${term}%,supplier_id.in.(${ids.join(',')})`)
    else q = q.ilike('reference', `%${term}%`)
  }
  // Header filters (AND with everything above). Empty arrays are skipped.
  if (filters.supplierIds?.length) q = q.in('supplier_id', filters.supplierIds)
  if (filters.methods?.length) q = q.in('payment_method', filters.methods)
  if (filters.amountMin != null) q = q.gte('amount', filters.amountMin)
  if (filters.amountMax != null) q = q.lte('amount', filters.amountMax)
  // Type is derived from purchase_order_id (payment = has PO, advance = no PO).
  // Only constrain when exactly one type is chosen; both/none = no filter.
  if (filters.types?.length === 1) {
    if (filters.types[0] === 'payment') q = q.not('purchase_order_id', 'is', null)
    else if (filters.types[0] === 'advance') q = q.is('purchase_order_id', null)
  }
  // Status is derived from is_voided (posted | voided). Same one-of-two logic.
  if (filters.statuses?.length === 1) {
    if (filters.statuses[0] === 'voided') q = q.is('is_voided', true)
    else if (filters.statuses[0] === 'posted') q = q.not('is_voided', 'is', true)
  }
  return q
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function mapRow(r: any): VendorPaymentRow {
  return {
    id: r.id,
    type: r.purchase_order_id ? 'payment' : 'advance',
    date: r.payment_date,
    supplierName: r.suppliers?.name ?? '—',
    method: r.payment_method ?? null,
    reference: r.reference ?? null,
    amount: Number(r.amount ?? 0),
    poNumber: r.purchase_orders?.order_number ?? null,
    journalId: r.journal_id ?? null,
    journalNumber: r.journal_entries?.entry_number ?? null,
    isVoided: !!r.is_voided,
  }
}

/** One page of vendor payments + the exact count of the filtered set. */
export async function fetchVendorPaymentsPage(
  tid: string,
  filters: VendorPaymentFilters,
  { page, pageSize }: { page: number; pageSize: number },
): Promise<{ rows: VendorPaymentRow[]; count: number }> {
  const supplierIds = await resolveSupplierIds(tid, filters.search)
  let q = db('supplier_payments').select(PAGE_SELECT, { count: 'exact' })
  q = applyVendorFilters(q, tid, filters, supplierIds)
  const { data, count, error } = await q
    .order('payment_date', { ascending: false })
    .order('id', { ascending: false })
    .range((page - 1) * pageSize, page * pageSize - 1)
  if (error) throw error
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return { rows: ((data ?? []) as any[]).map(mapRow), count: count ?? 0 }
}

/** Footer sum across ALL filtered rows (not just the page); voided always excluded. */
export async function fetchVendorPaymentsTotal(tid: string, filters: VendorPaymentFilters): Promise<number> {
  const supplierIds = await resolveSupplierIds(tid, filters.search)
  const rows = await fetchAllPaged<{ amount: number; is_voided: boolean }>((from, to) =>
    applyVendorFilters(db('supplier_payments').select('amount, is_voided'), tid, filters, supplierIds)
      .order('id', { ascending: true })
      .range(from, to),
  )
  return rows.filter(r => !r.is_voided).reduce((s, r) => s + Number(r.amount ?? 0), 0)
}

export interface VendorPaymentExportRow extends Record<string, unknown> {
  date: string
  supplier: string
  type: string
  method: string
  reference: string
  amount: number
  po_reference: string
  journal: string
  status: string
}

export function toVendorExportRow(r: VendorPaymentRow): VendorPaymentExportRow {
  return {
    date: displayDate(r.date),
    supplier: r.supplierName,
    type: r.type === 'advance' ? 'Advance' : 'Payment',
    method: r.method ?? '',
    reference: r.reference ?? '',
    amount: r.amount,
    po_reference: r.poNumber ?? '',
    journal: r.journalNumber ?? r.journalId ?? '',
    status: r.isVoided ? 'Voided' : 'Posted',
  }
}

/** All filtered rows, mapped for CSV/Excel export (decision #8: every matching row). */
export async function fetchVendorPaymentsExport(tid: string, filters: VendorPaymentFilters): Promise<VendorPaymentExportRow[]> {
  const supplierIds = await resolveSupplierIds(tid, filters.search)
  const rows = await fetchAllPaged<Record<string, unknown>>((from, to) =>
    applyVendorFilters(db('supplier_payments').select(PAGE_SELECT), tid, filters, supplierIds)
      .order('payment_date', { ascending: false })
      .order('id', { ascending: false })
      .range(from, to),
  )
  return rows.map(mapRow).map(toVendorExportRow)
}
